import { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../layout/AdminLayoutSewa";
import Swal from "sweetalert2";
import { adminAgentKpiRanking } from "../../api";

function monthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function money(v) {
  return Number(v || 0).toFixed(2);
}

export default function KPIRevenue() {
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(monthStr());
  const [summary, setSummary] = useState({
    month,
    total_agents: 0,
    total_collected_amount: 0,
  });
  const [top3, setTop3] = useState([]);
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await adminAgentKpiRanking({ month });
      setSummary(res?.summary || {});
      setTop3(Array.isArray(res?.top3) ? res.top3 : []);
      setItems(Array.isArray(res?.items) ? res.items : []);
    } catch (error) {
      console.error(error);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: error?.response?.data?.error || "Gagal load KPI pegawai hasil.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [month]);

  const chartMax = useMemo(() => {
    const max = Math.max(...items.map((x) => Number(x.collected_amount || 0)), 0);
    return max || 1;
  }, [items]);

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Ranking Report</h2>
              <p className="text-sm text-gray-500 mt-1">
                KPI kutipan pegawai hasil mengikut bulan.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="border rounded-md p-2 text-sm bg-white"
              />
              <button
                onClick={load}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Top 3 Agents</h3>
          {top3.length === 0 ? (
            <div className="text-sm text-gray-500">Tiada data.</div>
          ) : (
            <ul className="space-y-2 text-gray-700">
              {top3.map((x) => (
                <li key={x.rank}>
                  <span className="font-semibold">Top {x.rank}:</span> {x.agent_name} — {x.collection_rate}%
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white p-5 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold text-gray-800 mb-6">Overall Agent Collection</h3>

          {items.length === 0 ? (
            <div className="text-sm text-gray-500">Tiada data.</div>
          ) : (
            <div className="space-y-6">
              <div className="h-72 flex items-end gap-10 border-b border-l px-6 pb-8 overflow-x-auto">
                {items.map((item) => {
                  const amount = Number(item.collected_amount || 0);
                  const heightPercent = Math.max(6, (amount / chartMax) * 100);

                  return (
                    <div key={item.agent_id} className="flex flex-col items-center min-w-[120px]">
                      <div className="text-xs text-gray-500 mb-2">RM {money(amount)}</div>
                      <div
                        className="w-12 rounded-t-md bg-blue-400"
                        style={{ height: `${heightPercent}%`, minHeight: "20px" }}
                        title={`${item.agent_name}: RM ${money(amount)}`}
                      />
                      <div className="text-sm text-gray-700 mt-2 text-center">{item.agent_name}</div>
                    </div>
                  );
                })}
              </div>

              <div className="text-center text-xs text-gray-500">
                Click any bar concept can be added later for monthly drilldown.
              </div>
            </div>
          )}
        </div>

        <div className="bg-white p-5 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Agent Collection Summary</h3>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2 border">Agent</th>
                  <th className="text-left p-2 border">Assigned Units</th>
                  <th className="text-left p-2 border">Total Bills</th>
                  <th className="text-left p-2 border">Paid</th>
                  <th className="text-left p-2 border">Unpaid</th>
                  <th className="text-left p-2 border">Overdue</th>
                  <th className="text-left p-2 border">Total Amount (RM)</th>
                  <th className="text-left p-2 border">Collected (RM)</th>
                  <th className="text-left p-2 border">Rate (%)</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="p-3 text-center text-gray-500">
                      Tiada data.
                    </td>
                  </tr>
                ) : (
                  items.map((r) => (
                    <tr key={r.agent_id} className="border-t hover:bg-gray-50">
                      <td className="p-2 border font-semibold">{r.agent_name}</td>
                      <td className="p-2 border">{r.assigned_units}</td>
                      <td className="p-2 border">{r.total_bills}</td>
                      <td className="p-2 border">{r.paid_bills}</td>
                      <td className="p-2 border">{r.unpaid_bills}</td>
                      <td className="p-2 border">{r.overdue_bills}</td>
                      <td className="p-2 border">{money(r.total_amount)}</td>
                      <td className="p-2 border">{money(r.collected_amount)}</td>
                      <td className="p-2 border">
                        <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-semibold">
                          {r.collection_rate}%
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}